

import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { DatabaseService } from '../lib/database-v2'
import { AuthCache } from '../utils/authCache'
import { ImmediateAuthCheck } from '../utils/immediateAuthCheck'
import { toast } from 'sonner'

interface AuthContextType {
  user: User | null
  session: Session | null
  isAuthenticated: boolean
  isLoading: boolean
  authInitialized: boolean
  hasAdminAccess: boolean
  hasSuperAdminAccess: boolean
  signIn: (email: string, password: string, options?: { rememberMe?: boolean }) => Promise<{ success: boolean; error?: string }>
  signUp: (email: string, password: string, userData?: any) => Promise<{ success: boolean; error?: string }>
  signInWithGoogle: () => Promise<{ success: boolean; error?: string; isNewUser?: boolean }>
  signOut: () => Promise<void>
  resendConfirmation: (email: string) => Promise<{ success: boolean; error?: string }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  // ALWAYS start with loading=true to show initial loading screen
  const [isLoading, setIsLoading] = useState(true)
  const [hasAdminAccess, setHasAdminAccess] = useState(false)
  const [hasSuperAdminAccess, setHasSuperAdminAccess] = useState(false)
  const [authInitialized, setAuthInitialized] = useState(false)

  const db = DatabaseService.getInstance()

  // Initialize auth state and listener
  useEffect(() => {
    let isMounted = true
    let initializationTimeout: NodeJS.Timeout
    const startTime = Date.now()
    const MIN_LOADING_TIME = 800 // Show loading screen for minimum 800ms

    const initializeAuth = async () => {
      try {
        // Always show loading screen for minimum duration for better UX
        const ensureMinimumLoadingTime = async (callback: () => void) => {
          const elapsed = Date.now() - startTime
          const remaining = MIN_LOADING_TIME - elapsed
          if (remaining > 0) {
            await new Promise(resolve => setTimeout(resolve, remaining))
          }
          callback()
        }

        // If immediate check suggests no auth, still show loading briefly
        if (!ImmediateAuthCheck.isLikelyAuthenticated()) {
          if (isMounted) {
            await ensureMinimumLoadingTime(() => {
              setIsLoading(false)
              setAuthInitialized(true)
            })
          }
          return
        }

        // For likely authenticated users, proceed with full auth check
        setIsLoading(true)

        // Check cache first to reduce perceived loading time
        const cachedAuth = AuthCache.get()
        if (cachedAuth && cachedAuth.isAuthenticated) {
        }

        // Set a maximum initialization time to prevent infinite loading
        initializationTimeout = setTimeout(() => {
          if (isMounted && !authInitialized) {
            console.warn('⚠️ Auth initialization timeout - proceeding without auth')
            AuthCache.clear() // Clear any invalid cache
            ImmediateAuthCheck.clearCache()
            setIsLoading(false)
            setAuthInitialized(true)
          }
        }, 5000) // 5 second timeout

        // Initialize our modern database service
        // Get current session with explicit timeout
        const sessionPromise = supabase.auth.getSession()
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session check timeout')), 3000)
        )

        const { data: { session: currentSession }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]) as any

        // Clear timeout since we got a response
        if (initializationTimeout) {
          clearTimeout(initializationTimeout)
        }
        
        if (error) {
          console.error('❌ Session check failed:', error.message)
          if (isMounted) {
            setIsLoading(false)
            setAuthInitialized(true)
          }
          return
        }

        if (currentSession && isMounted) {
          await handleAuthStateChange(currentSession, true)
        } else {
          if (isMounted) {
            setIsLoading(false)
            setAuthInitialized(true)
          }
        }

      } catch (error) {
        console.error('❌ Auth initialization failed:', error)
        if (initializationTimeout) {
          clearTimeout(initializationTimeout)
        }
        if (isMounted) {
          setIsLoading(false)
          setAuthInitialized(true)
        }
      }
    }

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: AuthChangeEvent, session: Session | null) => {
        if (isMounted) {
          if (event === 'SIGNED_IN' && session) {
            await handleAuthStateChange(session, false)
          } else if (event === 'SIGNED_OUT' || !session) {
            handleSignOut()
          } else if (event === 'TOKEN_REFRESHED' && session) {
            setSession(session)
            setUser(session.user)
            // Don't show loading for token refresh
          }
        }
      }
    )

    initializeAuth()

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [])

  const handleAuthStateChange = async (newSession: Session, isInitial: boolean) => {
    try {
      // Keep loading state active during profile setup for initial loads
      if (isInitial) {
        setIsLoading(true)
      }

      setSession(newSession)
      setUser(newSession.user)

      // Cache auth state for faster subsequent loads
      AuthCache.set(newSession.user.id, newSession.user.email || '', true)

      // Ensure user profile exists in new database schema
      const profile = await db.getUserProfile(newSession.user.id)
      if (!profile) {
        await db.createUserProfile(newSession.user, {
          first_name: newSession.user.user_metadata?.first_name,
          last_name: newSession.user.user_metadata?.last_name
        })
      }

      // Check permissions
      const [isAdmin, isSuperAdmin] = await Promise.all([
        db.isAdmin(newSession.user.id),
        db.isSuperAdmin(newSession.user.id)
      ])

      setHasAdminAccess(isAdmin)
      setHasSuperAdminAccess(isSuperAdmin)

      if (!isInitial) {
        toast.success(`Welcome back, ${newSession.user.email}!`)
      }

      console.log(`✅ Auth complete for: ${newSession.user.email}${isAdmin ? ' (Admin)' : ''}${isSuperAdmin ? ' (Super Admin)' : ''}`)

      // Mark auth as initialized and stop loading
      if (!authInitialized) {
        setAuthInitialized(true)
      }
      setIsLoading(false)

    } catch (error) {
      console.error('❌ Auth state change handling failed:', error)
      AuthCache.clear() // Clear cache on error
      if (!authInitialized) {
        setAuthInitialized(true)
      }
      setIsLoading(false)
    }
  }

  const handleSignOut = () => {
    setUser(null)
    setSession(null)
    setHasAdminAccess(false)
    setHasSuperAdminAccess(false)
    setAuthInitialized(true) // Keep initialized state
    setIsLoading(false)
    AuthCache.clear() // Clear auth cache on signout
    ImmediateAuthCheck.clearCache() // Clear immediate auth cache
  }

  const signIn = async (email: string, password: string, options?: { rememberMe?: boolean }): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true)

      // Configure session persistence based on rememberMe
      if (options?.rememberMe) {
        // Set persistent session (30 days)
        await supabase.auth.setSession({
          access_token: '',
          refresh_token: '',
        })
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password
      })

      if (error) {
        console.error('❌ Sign in failed:', error.message)
        return { success: false, error: error.message }
      }

      if (!data.session || !data.user) {
        return { success: false, error: 'No session created' }
      }

      // 🎯 GRACE PERIOD RESET LOGIC - Check if user needs grace period reset
      try {
        const shouldReset = await db.shouldResetGracePeriod(data.user.id);
        if (shouldReset) {
          await db.resetGracePeriod(data.user.id);
          toast.success('Welcome back! Your grace period has been extended.');
        }
      } catch (gracePeriodError) {
        console.error('⚠️ Grace period check failed:', gracePeriodError);
        // Don't block login for grace period errors
      }

      // Store rememberMe preference in session metadata if needed
      if (options?.rememberMe) {
        localStorage.setItem('clockit-remember-me', 'true')
      } else {
        localStorage.removeItem('clockit-remember-me')
      }

      // 🎯 GRACE PERIOD RESET LOGIC - Check if user needs grace period reset
      try {
        const shouldReset = await db.shouldResetGracePeriod(data.user.id);
        if (shouldReset) {
          await db.resetGracePeriod(data.user.id);
          toast.success('Welcome back! Your grace period has been extended.');
        }
      } catch (gracePeriodError) {
        console.error('⚠️ Grace period check failed:', gracePeriodError);
        // Don't block login for grace period errors
      }

      // Store rememberMe preference in session metadata if needed
      if (options?.rememberMe) {
        localStorage.setItem('clockit-remember-me', 'true')
      } else {
        localStorage.removeItem('clockit-remember-me')
      }

      // Auth state will be updated by the listener
      return { success: true }

    } catch (error: any) {
      console.error('❌ Sign in error:', error)
      return { success: false, error: error.message || 'Sign in failed' }
    } finally {
      setIsLoading(false)
    }
  }

  const signUp = async (email: string, password: string, userData?: any): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true)

      const { data, error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          data: userData || {}
        }
      })

      if (error) {
        console.error('❌ Sign up failed:', error.message)
        return { success: false, error: error.message }
      }

      if (data.user && !data.session) {
        // Email confirmation required
        toast.info('Please check your email to confirm your account')
        return { success: true }
      }

      if (data.session) {
        // Immediate sign in (email confirmation disabled)
        return { success: true }
      }

      return { success: false, error: 'Unexpected signup response' }

    } catch (error: any) {
      console.error('❌ Sign up error:', error)
      return { success: false, error: error.message || 'Sign up failed' }
    } finally {
      setIsLoading(false)
    }
  }

  const signInWithGoogle = async (): Promise<{ success: boolean; error?: string; isNewUser?: boolean }> => {
    try {
      setIsLoading(true)
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      })

      if (error) {
        console.error('❌ Google OAuth error:', error.message)
        return { success: false, error: error.message }
      }

      // For OAuth, the redirect happens automatically
      // The actual session handling will be done in the callback
      return { success: true }

    } catch (error: any) {
      console.error('❌ Google OAuth error:', error)
      return { success: false, error: error.message || 'Google sign in failed' }
    } finally {
      setIsLoading(false)
    }
  }

  const signOut = async (): Promise<void> => {
    try {
      setIsLoading(true)

      const { error } = await supabase.auth.signOut()

      if (error) {
        console.error('❌ Sign out failed:', error.message)
        toast.error('Sign out failed')
      } else {
        toast.success('Signed out successfully')
      }

    } catch (error: any) {
      console.error('❌ Sign out error:', error)
      toast.error('Sign out failed')
    } finally {
      setIsLoading(false)
    }
  }

  const resendConfirmation = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email.toLowerCase().trim()
      })

      if (error) {
        console.error('❌ Resend confirmation failed:', error.message)
        return { success: false, error: error.message }
      }
      toast.success('Confirmation email sent!')
      return { success: true }

    } catch (error: any) {
      console.error('❌ Resend confirmation error:', error)
      return { success: false, error: error.message || 'Failed to resend confirmation' }
    }
  }

  const value: AuthContextType = {
    user,
    session,
    isAuthenticated: !!user && !!session,
    isLoading,
    authInitialized,
    hasAdminAccess,
    hasSuperAdminAccess,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resendConfirmation
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}